import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DiagnosticResult {
  keyExists?: boolean;
  keyFormat?: string;
  stripeInitialized?: boolean;
  error?: string | null;
  message?: string;
  sessionId?: string;
  url?: string;
}

const StripeDiagnostics = () => {
  const [verifyResult, setVerifyResult] = useState<DiagnosticResult | null>(null);
  const [checkoutResult, setCheckoutResult] = useState<DiagnosticResult | null>(null);
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  const checkStripeKey = async () => {
    setIsLoadingVerify(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      
      const response = await fetch('https://ypmennmpmhttnsgeviul.supabase.co/functions/v1/verify-stripe-key', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      const data = await response.json();
      console.log('Stripe key verification result:', data);
      setVerifyResult(data);
    } catch (error) {
      console.error('Error checking Stripe key:', error);
      setVerifyResult({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'Failed to connect to verification endpoint' 
      });
    } finally {
      setIsLoadingVerify(false);
    }
  };

  const testCheckoutSession = async () => {
    setIsLoadingCheckout(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      
      const testData = {
        itineraryId: '00000000-0000-0000-0000-000000000000',
        itineraryTitle: 'Test Itinerary',
        price: 10.00,
        currency: 'USD',
        userId: '00000000-0000-0000-0000-000000000000',
        successUrl: window.location.origin + '/purchase-success?test=true',
        cancelUrl: window.location.origin + '/dashboard/stripe-diagnostics',
      };

      const response = await fetch('https://ypmennmpmhttnsgeviul.supabase.co/functions/v1/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(testData),
      });
      
      const data = await response.json();
      console.log('Checkout session result:', data);
      setCheckoutResult(data);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setCheckoutResult({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'Failed to connect to checkout endpoint' 
      });
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const StatusIcon = ({ success, pending }: { success?: boolean; pending?: boolean }) => {
    if (pending) return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    return success ? 
      <CheckCircle className="h-5 w-5 text-green-500" /> : 
      <XCircle className="h-5 w-5 text-red-500" />;
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Stripe Integration Diagnostics</CardTitle>
          <CardDescription>
            Use these tools to check your Stripe integration and troubleshoot issues
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-2">Stripe Key Verification</h3>
            <Button 
              onClick={checkStripeKey} 
              disabled={isLoadingVerify}
              variant="outline"
            >
              {isLoadingVerify ? 'Checking...' : 'Verify Stripe Key'}
            </Button>
            
            {verifyResult && (
              <div className="mt-4 p-4 border rounded-md bg-muted">
                <h4 className="font-medium">Results:</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <StatusIcon success={verifyResult.keyExists} />
                    <span>API Key Exists: {verifyResult.keyExists ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusIcon success={verifyResult.keyFormat === 'valid_format'} />
                    <span>API Key Format: {verifyResult.keyFormat === 'valid_format' ? 'Valid' : 'Invalid'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusIcon success={verifyResult.stripeInitialized} />
                    <span>Stripe Initialized: {verifyResult.stripeInitialized ? 'Yes' : 'No'}</span>
                  </div>
                  {verifyResult.error && (
                    <div className="text-red-500 mt-2">
                      <p className="font-medium">Error:</p>
                      <p className="text-sm break-all">{verifyResult.error}</p>
                    </div>
                  )}
                  {verifyResult.message && (
                    <div className="mt-2">
                      <p className="font-medium">Message:</p>
                      <p className="text-sm">{verifyResult.message}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <Separator />
          
          <div>
            <h3 className="text-lg font-medium mb-2">Test Checkout Session Creation</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will attempt to create a test checkout session with minimal data
            </p>
            <Button 
              onClick={testCheckoutSession} 
              disabled={isLoadingCheckout}
              variant="outline"
            >
              {isLoadingCheckout ? 'Creating...' : 'Test Checkout Session'}
            </Button>
            
            {checkoutResult && (
              <div className="mt-4 p-4 border rounded-md bg-muted">
                <h4 className="font-medium">Results:</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <StatusIcon success={!!checkoutResult.sessionId} />
                    <span>Session Created: {checkoutResult.sessionId ? 'Yes' : 'No'}</span>
                  </div>
                  {checkoutResult.sessionId && (
                    <div className="mt-2">
                      <p className="font-medium">Session ID:</p>
                      <p className="text-sm break-all">{checkoutResult.sessionId}</p>
                    </div>
                  )}
                  {checkoutResult.url && (
                    <div className="mt-2">
                      <p className="font-medium">Checkout URL:</p>
                      <p className="text-sm break-all">{checkoutResult.url}</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => window.open(checkoutResult.url, '_blank')}
                      >
                        Open Checkout
                      </Button>
                    </div>
                  )}
                  {checkoutResult.error && (
                    <div className="text-red-500 mt-2">
                      <p className="font-medium">Error:</p>
                      <p className="text-sm break-all">{checkoutResult.error}</p>
                    </div>
                  )}
                  {checkoutResult.message && (
                    <div className="mt-2">
                      <p className="font-medium">Message:</p>
                      <p className="text-sm">{checkoutResult.message}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col items-start">
          <p className="text-sm text-muted-foreground">
            If you see errors, check the Stripe key in your Supabase secrets.
            Make sure it's correctly formatted and has the necessary permissions.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default StripeDiagnostics;
